import api from "@/api/axiosConfig";
import { ClientSelectSingle } from "@/components/form-v2/searchable-select/ClientSelect";
import { DepartmentSelectSingle } from "@/components/form-v2/searchable-select/DepartmentSelect";
import { Client, Department } from "@/types/userManagement";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

type StatusKind = "info" | "success" | "error" | null;
type BusyKind = "client-upload" | "client-view" | "client-delete" | "dept-upload" | "dept-view" | "dept-delete" | null;

function useStatus() {
  const [status, setStatus] = useState<{ kind: StatusKind; message: string }>({ kind: null, message: "" });
  const hideTimer = useRef<number | undefined>(undefined);

  const show = useCallback((kind: StatusKind, message: string) => {
    setStatus({ kind, message });
    if (hideTimer.current !== undefined) window.clearTimeout(hideTimer.current);
    if (kind !== "error") {
      hideTimer.current = window.setTimeout(() => setStatus({ kind: null, message: "" }), 4000);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimer.current !== undefined) {
        window.clearTimeout(hideTimer.current);
      }
    };
  }, []);

  return { status, show, clear: () => setStatus({ kind: null, message: "" }) };
}


function extractError(err: unknown) {
  const anyErr = err as any;
  if (anyErr?.response?.data?.message) return String(anyErr.response.data.message);
  if (anyErr?.response?.status) return `HTTP ${anyErr.response.status}`;
  if (anyErr?.message) return String(anyErr.message);
  return "Unexpected error";
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-2">{title}</h2>
        <p className="text-sm text-gray-700 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button className="px-4 py-2 rounded bg-gray-200" onClick={onCancel}>{cancelText}</button>
          <button className="px-4 py-2 rounded bg-red text-white" onClick={onConfirm}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}

export default function StandardOperatingProcedure() {
  const [client, setClient] = useState<Client>();
  const [department, setDepartment] = useState<Department>();
  const { status, show, clear } = useStatus();
  const [busy, setBusy] = useState<BusyKind>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const clientFileInput = useRef<HTMLInputElement>(null);
  const deptFileInput = useRef<HTMLInputElement>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmResolver = useRef<(ok: boolean) => void>();
  const askConfirm = useCallback((title: string, message: string, confirmText?: string) => {
    setConfirmOpen(true);
    return new Promise<boolean>((resolve) => {
      confirmResolver.current = resolve;
    }).finally(() => setConfirmOpen(false));
  }, []);
  const handleConfirmOk = () => confirmResolver.current?.(true);
  const handleConfirmCancel = () => confirmResolver.current?.(false);

  const bannerClasses = useMemo(
    () =>
      status.kind === "success"
        ? "bg-green text-white"
        : status.kind === "error"
        ? "bg-red text-white"
        : "bg-blue text-white",
    [status.kind]
  );

  const resetFileInput = (input: React.RefObject<HTMLInputElement>) => {
    if (input.current) input.current.value = "";
  };

  const triggerClientUpload = () => clientFileInput.current?.click();
  const triggerDeptUpload = () => deptFileInput.current?.click();

  const handleClientChange = (newClient: Client | undefined) => {
    setClient(newClient);
    setDepartment(undefined);
  };

  const handleClientUploadChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!client) {
      show("error", "Select a client first");
      resetFileInput(clientFileInput);
      return;
    }
    const file = event.target.files?.[0];
    if (!file) {
      show("error", "No file selected");
      return;
    }
    const formData = new FormData();
    formData.append("clientID", client.id);
    if (department?.id) formData.append("departmentID", department.id);
    formData.append("file", file);
    try {
      setBusy("client-upload");
      setUploadProgress(0);
      await api.post("/files/sop", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => {
          if (p.total) setUploadProgress(Math.round((p.loaded / p.total) * 100));
        },
      });
      show("success", department ? "Department SOP uploaded" : "Client SOP uploaded");
    } catch (err) {
      show("error", `Upload failed: ${extractError(err)}`);
    } finally {
      setBusy(null);
      setUploadProgress(null);
      resetFileInput(clientFileInput);
    }
  };

  const handleDeptUploadChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!client) {
      show("error", "Select a client first");
      resetFileInput(deptFileInput);
      return;
    }
    if (!department) {
      show("error", "Select a department first");
      resetFileInput(deptFileInput);
      return;
    }
    const file = event.target.files?.[0];
    if (!file) {
      show("error", "No file selected");
      return;
    }
    const formData = new FormData();
    formData.append("clientID", client.id);
    formData.append("departmentID", department.id);
    formData.append("file", file);
    try {
      setBusy("dept-upload");
      setUploadProgress(0);
      await api.post("/files/sop", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => {
          if (p.total) setUploadProgress(Math.round((p.loaded / p.total) * 100));
        },
      });
      show("success", "Department SOP uploaded");
    } catch (err) {
      show("error", `Upload failed: ${extractError(err)}`);
    } finally {
      setBusy(null);
      setUploadProgress(null);
      resetFileInput(deptFileInput);
    }
  };

  const handleClientView = async () => {
    if (!client) {
      show("error", "Select a client first");
      return;
    }
    try {
      setBusy("client-view");
      show("info", department ? "Opening department SOP…" : "Opening client SOP…");
      const params: Record<string, string> = department?.id ? { clientID: client.id, departmentID: department.id } : { clientID: client.id };
      const response = await api.get("/files/sop", { params, responseType: "blob" });
      const fileURL = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(fileURL, "_blank");
    } catch (err) {
      show("error", `View failed: ${extractError(err)}`);
    } finally {
      setBusy(null);
    }
  };

  const handleDepartmentView = async () => {
    if (!client || !department) {
      show("error", "Select client and department first");
      return;
    }
    try {
      setBusy("dept-view");
      show("info", "Opening department SOP…");
      const response = await api.get("/files/sop", {
        params: { clientID: client.id, departmentID: department.id },
        responseType: "blob",
      });
      const fileURL = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(fileURL, "_blank");
    } catch (err) {
      show("error", `View failed: ${extractError(err)}`);
    } finally {
      setBusy(null);
    }
  };

  const handleClientDelete = async () => {
    if (!client) {
      show("error", "Select a client first");
      return;
    }
    const title = department ? "Delete Department SOP?" : "Delete Client SOP?";
    const message = department
      ? `This will delete the SOP for ${client.name} • ${department.name}.`
      : `This will delete the SOP for ${client.name}.`;
    const ok = await askConfirm(title, message);
    if (!ok) return;
    try {
      setBusy("client-delete");
      const params: Record<string, string> = department?.id ? { clientID: client.id, departmentID: department.id } : { clientID: client.id };
      await api.delete("/files/sop", { params });
      show("success", department ? "Department SOP deleted" : "Client SOP deleted");
    } catch (err) {
      show("error", `Delete failed: ${extractError(err)}`);
    } finally {
      setBusy(null);
    }
  };

  const handleDepartmentDelete = async () => {
    if (!client || !department) {
      show("error", "Select client and department first");
      return;
    }
    const ok = await askConfirm("Delete Department SOP?", `This will delete the SOP for ${client.name} • ${department.name}.`);
    if (!ok) return;
    try {
      setBusy("dept-delete");
      await api.delete("/files/sop", { params: { clientID: client.id, departmentID: department.id } });
      show("success", "Department SOP deleted");
    } catch (err) {
      show("error", `Delete failed: ${extractError(err)}`);
    } finally {
      setBusy(null);
    }
  };

  const isUploading = busy === "client-upload" || busy === "dept-upload";

  return (
    <div className="container mx-auto grid grid-rows-2 gap-6 p-4">
      {status.kind && (
        <div className={`rounded px-4 py-3 mb-2 flex items-center justify-between ${bannerClasses}`} role="status" aria-live="polite">
          <div className="truncate">{status.message}</div>
          <button className="ml-4 underline" onClick={clear} aria-label="Dismiss">Close</button>
        </div>
      )}

      {uploadProgress !== null && (
        <div className="w-full h-2 rounded bg-gray-200 overflow-hidden">
          <div className="h-2 bg-blue transition-all" style={{ width: `${uploadProgress}%` }} />
        </div>
      )}

      <div className="grid grid-cols-4 gap-6 w-full items-center">
        <div className="col-span-1">
          <ClientSelectSingle client={client} onChange={handleClientChange} />
        </div>
        <div className="col-span-3 flex gap-4">
          {client && (
            <>
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleClientUploadChange}
                ref={clientFileInput}
                disabled={busy !== null}
              />
              <button
                className="bg-blue text-white rounded px-4 py-2 w-full disabled:opacity-60"
                onClick={triggerClientUpload}
                disabled={busy !== null}
              >
                {busy === "client-upload" ? `Uploading ${uploadProgress ?? 0}%` : "Upload"}
              </button>
              <button
                className="bg-green text-white rounded px-4 py-2 w-full disabled:opacity-60"
                onClick={handleClientView}
                disabled={busy !== null}
              >
                {busy === "client-view" ? "Opening…" : "View Client SOP"}
              </button>
              <button
                className="bg-red text-white rounded px-4 py-2 w-full disabled:opacity-60"
                onClick={handleClientDelete}
                disabled={busy !== null}
              >
                {busy === "client-delete" ? "Deleting…" : "Delete Client SOP"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-6 w-full items-center">
        {client && (
          <div className="col-span-1">
            <DepartmentSelectSingle clientID={client.id} department={department} onChange={setDepartment} />
          </div>
        )}
        <div className="col-span-3 flex gap-4">
          {department && (
            <>
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleDeptUploadChange}
                ref={deptFileInput}
                disabled={busy !== null}
              />
              <button
                className="bg-blue text-white rounded px-4 py-2 w-full disabled:opacity-60"
                onClick={triggerDeptUpload}
                disabled={busy !== null}
              >
                {busy === "dept-upload" ? `Uploading ${uploadProgress ?? 0}%` : "Upload"}
              </button>
              <button
                className="bg-green text-white rounded px-4 py-2 w-full disabled:opacity-60"
                onClick={handleDepartmentView}
                disabled={busy !== null}
              >
                {busy === "dept-view" ? "Opening…" : "View Dept SOP"}
              </button>
              <button
                className="bg-red text-white rounded px-4 py-2 w-full disabled:opacity-60"
                onClick={handleDepartmentDelete}
                disabled={busy !== null}
              >
                {busy === "dept-delete" ? "Deleting…" : "Delete Dept SOP"}
              </button>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Are you sure?"
        message=""
        confirmText="Delete"
        onConfirm={handleConfirmOk}
        onCancel={handleConfirmCancel}
      />
    </div>
  );
}
